import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import { RemoveFromBatchModalProps, RemoveFromBatchStatus } from './RemoveFromBatchModal.types'
import { ActionModal, Modal, Text, Spacer, AcknowledgeModal, FormField, Select, TextArea } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useRemoveFromBatchForm } from './useRemoveFromBatchForm/useRemoveFromBatchForm'

export function RemoveFromBatchModal({ isOpen, onClose, disbursement, refreshData }: RemoveFromBatchModalProps) {
  const { disbursementsService } = useServices()
  const { t } = useTranslation()

  const [status, setStatus] = useState<RemoveFromBatchStatus | null>(null)
  const [selectedReason, setSelectedReason] = useState<string>('')

  const cancelWithReason = ({ id, reason }: { id: string; reason?: string }) =>
    disbursementsService.cancelDisbursement(id, reason)

  const [{ isPending }, { apiFunc: cancelDisbursement }] = useMutation(
    cancelWithReason,
    () => {
      setStatus(RemoveFromBatchStatus.SUCCESS)
      refreshData?.()
    },
    () => setStatus(RemoveFromBatchStatus.ERROR)
  )

  const { formik, reasonOptions } = useRemoveFromBatchForm({
    onFormSubmit: async ({ reason }) => cancelDisbursement({ id: disbursement.id, reason }),
    disbursementTypes: [disbursement.type]
  })

  const onReasonChange = (reason: string) => {
    setSelectedReason(reason)
    formik.setFieldValue('cancelReason', reason)
  }

  const { values, touched, errors, isValid, handleChange, handleBlur } = formik

  const successModal = status === RemoveFromBatchStatus.SUCCESS && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.removeFromBatch.success.title')}
      buttonText={t('button.close')}
      handleClose={onClose}
      viewTestId="remove-from-batch-success-modal"
      buttonTestId="close-remove-from-batch-success-modal"
    />
  )

  const errorModal = status === RemoveFromBatchStatus.ERROR && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.removeFromBatch.error.title')}
      buttonText={t('button.close')}
      handleClose={onClose}
      viewTestId="remove-from-batch-error-modal"
      buttonTestId="close-remove-from-batch-error-modal"
    />
  )

  return (
    <Modal open={isOpen} onClose={onClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('dialog.removeFromBatch.header')}
          primaryButtonText={t('button.remove')}
          loadingText={t('button.removing')}
          secondaryButtonText={t('button.cancel')}
          type="submit"
          handleClose={onClose}
          handleSubmit={() => formik.handleSubmit()}
          primaryBtnTestId="remove-from-batch-submit-button"
          secondaryBtnTestId="remove-from-batch-cancel-button"
          disabled={!isValid || isPending}
          loading={isPending}
        >
          <div>
            <Text size="small">
              {t('dialog.removeFromBatch.reasonDescription', {
                foundationName: disbursement.organisationName
              })}
            </Text>
            <Spacer axis="vertical" size={6} />
            <form>
              <FormField
                label={t('dialog.removeFromBatch.removalReason')}
                data-testid="cancelReason"
                necessity="required"
              >
                <Select
                  data-testid="cancelReasonSelect"
                  placeholder={t('dialog.removeFromBatch.selectReason')}
                  options={reasonOptions}
                  defaultValue={reasonOptions.find(option => option.value === values.cancelReason) ?? undefined}
                  onChange={({ value }) => onReasonChange(value)}
                  maxDropdownHeight="200px"
                />
              </FormField>
              <Spacer axis="vertical" size={6} />
              {selectedReason === 'Other' && (
                <FormField
                  label={t('dialog.removeFromBatch.customReason')}
                  necessity="required"
                  status={touched.customReason && errors.customReason ? 'danger' : 'default'}
                  statusMessage={errors.customReason || ''}
                  data-testid="customReason"
                >
                  <TextArea
                    name="customReason"
                    value={values.customReason || ''}
                    placeholder={t('dialog.removeFromBatch.customReasonPlaceholder')}
                    maxLength={255}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </FormField>
              )}
            </form>
          </div>
        </ActionModal>
      )}
    </Modal>
  )
}
